import { Colors } from 'src/components/theme'
import { MapSigns } from '@emotion-icons/fa-solid/MapSigns'
import { MapMarkerAlt } from '@emotion-icons/fa-solid/MapMarkerAlt'
import { Chip } from 'src/v2/components/chip'
import { Briefcase } from '@emotion-icons/fa-solid/Briefcase'
import { MapSquareIcon } from 'src/components/icons/map-square'
import { ShoppingCenterIcon } from 'src/components/insights/icons/shopping-center'
import { User } from '@emotion-icons/fa-solid/User'
import { Building } from '@emotion-icons/fa-solid/Building'
import { Question } from '@emotion-icons/octicons'
import { ParcelTypeChip } from 'src/v2/components/parcel-type-chip'
import { PARCEL_TYPES_BY_MCODE_MAP } from 'src/constants/cherre-data-dictionary/zoning-colors'
import { ParcelSummary } from 'src/v2/views/owner-reveal/sidebar/parcel-summary'
import { formatString } from 'src/utils/format-string'
import { BrandLogo } from 'src/components/insights/location-visits/stats/location-logo'
import { getCategoryFromNaicsCode } from 'src/constants/mapbox'
import { getOwnerTypeLabel, parseEntityDistribution } from 'src/v2/utils/entity'
import { Typography } from '@mui/material'
import { OwnerSummary } from 'src/v2/views/owner-reveal/sidebar/owner-summary'
import { AMPossibleResults, AMSearchResults } from 'src/common-services'
import { prettyNumber } from 'src/utils/number-format'
import { SelectedEntity } from 'src/v2/types/core'
import { POI_CATEGORIES_MAP } from 'src/constants/poi-categories'
import { NearbyPoint } from 'src/views/map-explorer/sidebar/panels/results/nearby-point'

export const checkIfNearbyResultsAreEmpty = (
  results: AMSearchResults,
  supportedSelections?: SelectedEntity['type'][]
) => {
  if (!supportedSelections) {
    return (
      results.nearby.locations.length === 0 &&
      results.nearby.shoppingCenters.length === 0 &&
      results.nearby.brands.length === 0 &&
      results.nearby.parcels.length === 0 &&
      results.nearby.entities.length === 0 &&
      results.nearby.addresses.length === 0 &&
      results.nearby.other.length === 0
    )
  }
  const keys = Object.keys(results.nearby)
  const allSupportedEmpty = keys.every((key) => {
    switch (key) {
      case 'locations':
        return !supportedSelections.includes('SELECTED_LOCATION') || results.nearby.locations.length === 0
      case 'shoppingCenters':
        return !supportedSelections.includes('SELECTED_SHOPPING_CENTER') || results.nearby.shoppingCenters.length === 0
      case 'brands':
        return !supportedSelections.includes('SELECTED_BRAND') || results.nearby.brands.length === 0
      case 'parcels':
        return !supportedSelections.includes('SELECTED_PARCEL') || results.nearby.parcels.length === 0
      case 'entities':
        return !supportedSelections.includes('SELECTED_OWNER') || results.nearby.entities.length === 0
      case 'addresses':
        return !supportedSelections.includes('SELECTED_ADDRESS') || results.nearby.addresses.length === 0
      case 'other':
        return !supportedSelections.includes('SELECTED_ADDRESS') || results.nearby.other.length === 0
      default:
        return true
    }
  })
  return allSupportedEmpty
}
export const checkIfParcelResultsAreEmpty = (results: AMSearchResults) => {
  return (
    results.byParcels.locations.length === 0 &&
    results.byParcels.shoppingCenters.length === 0 &&
    results.byParcels.brands.length === 0 &&
    results.byParcels.parcels.length === 0 &&
    results.byParcels.entities.length === 0
  )
}

export const checkIfResultsAreCompletelyEmpty = (results: AMSearchResults) => {
  return checkIfNearbyResultsAreEmpty(results) && checkIfParcelResultsAreEmpty(results)
}

type BuildLocationResultsOptions = {
  sort?: boolean
  iconSize?: number
  brandIconSize?: number
  dynamicIconColor?: boolean
}
export const buildLocationResults = (
  results: AMPossibleResults['locations'],
  options?: BuildLocationResultsOptions
) => {
  let locations = results
  if (options?.sort) {
    const ordered = Array.from(POI_CATEGORIES_MAP.entries())
    const categoryOrder = Object.fromEntries(ordered.map(([key, value], index) => [key, index]))
    locations = locations.sort((a, b) => {
      const aCategory = getCategoryFromNaicsCode(a.meta.naicsCode)?.category.toUpperCase()
      const bCategory = getCategoryFromNaicsCode(b.meta.naicsCode)?.category.toUpperCase()
      const aIndex = categoryOrder[aCategory] !== undefined ? categoryOrder[aCategory] : Infinity
      const bIndex = categoryOrder[bCategory] !== undefined ? categoryOrder[bCategory] : Infinity
      return aIndex - bIndex || a.title.localeCompare(b.title)
    })
  }

  return locations.map((location) => ({
    id: location.id,
    title: location.title,
    subtitle: location.subtitle || '',
    icon: location.meta.brandId ? (
      <BrandLogo brandId={location.meta.brandId} noBorder size={options?.brandIconSize || options?.iconSize || 14} />
    ) : (
      <MapMarkerAlt
        size={options?.iconSize || 14}
        color={options?.dynamicIconColor ? getCategoryFromNaicsCode(location.meta.naicsCode)?.color : Colors.neutral50}
      />
    ),
    color: location.meta.brandId
      ? 'transparent'
      : getCategoryFromNaicsCode(location.meta.naicsCode)?.color || Colors.neutral400,
    header: undefined,
    type: location.type,
    point: location.meta.point,
  }))
}
export const buildShoppingCenterResults = (results: AMPossibleResults['shoppingCenters']) => {
  return results.map((shoppingCenter) => ({
    id: shoppingCenter.id,
    title: shoppingCenter.title,
    subtitle: shoppingCenter.subtitle || '',
    icon: <ShoppingCenterIcon size={16} color="#fff" />,
    color: Colors.primaryBlue500,
    header: undefined,
    footer: undefined,
    type: shoppingCenter.type,
    point: shoppingCenter.meta.point,
  }))
}
const buildBrandResults = (results: AMPossibleResults['brands']) => {
  return results.map((brand) => ({
    id: brand.id,
    title: brand.title,
    subtitle: `Locations: ${prettyNumber(brand.meta.locationsCount)}`,
    icon: <BrandLogo brandId={brand.id} />,
    color: Colors.purpleDark,
    type: brand.type,
  }))
}
const buildAddressResults = (results: AMSearchResults['nearby']['addresses']) => {
  return results.map((address) => ({
    id: address.id,
    header: undefined,
    title: address.title,
    subtitle: address.subtitle?.replace(', United States', '').replace(', USA', '') || '',
    icon: <MapSigns size={14} />,
    color: Colors.neutral400,
    type: address.type,
    point: address.meta.point,
    additional:
      results.length === 1 ? (
        <NearbyPoint
          request={{
            points: [
              {
                point: address.meta.point,
                address: address.subtitle,
                name: address.title,
              },
            ],
          }}
        />
      ) : undefined,
  }))
}
const buildParcelResults = (results: AMPossibleResults['parcels']) => {
  return results.map((parcel) => ({
    id: parcel.id,
    title: formatString(parcel.title, 'address'),
    subtitle: parcel.subtitle || '',
    color: Colors.primaryGreen700,
    icon: <MapSquareIcon size={12} />,
    header: parcel.meta.mCode ? <ParcelTypeChip type={PARCEL_TYPES_BY_MCODE_MAP[parcel.meta.mCode]} /> : undefined,
    footer: (
      <ParcelSummary
        amountOfContacts={parcel.meta.owners.length}
        brandIds={parcel.meta.brandedLocations.map((location) => location.brandId || '')}
      />
    ),
    type: parcel.type,
  }))
}
const buildEntityResults = (results: AMPossibleResults['entities']) => {
  return results.map((entity) => ({
    id: entity.id,
    title: formatString(entity.title, 'name'),
    subtitle: formatString(entity.subtitle, 'address'),
    header: (
      <Chip
        icon={
          getOwnerTypeLabel(entity.meta.type) === 'Individual'
            ? User
            : getOwnerTypeLabel(entity.meta.type) === 'Organization'
              ? Building
              : Question
        }
        backgroundColor={Colors.neutral100}>
        <Typography variant="caption1" color={Colors.neutral500}>
          {getOwnerTypeLabel(entity.meta.type)}
        </Typography>
      </Chip>
    ),
    footer: (
      <OwnerSummary
        amountOfContacts={entity.meta.amountOfContacts}
        amountOfParcels={entity.meta.amountOfParcels}
        distributions={parseEntityDistribution(entity.meta)}
      />
    ),
    icon: <Briefcase size={14} />,
    color: Colors.primaryRed800,
    type: entity.type,
  }))
}

export const buildSearchResults = (results: AMSearchResults, supportedSelections?: SelectedEntity['type'][]) => {
  const { nearby, byParcels } = results
  const nearbyResults = {
    locations:
      !supportedSelections || supportedSelections.includes('SELECTED_LOCATION')
        ? buildLocationResults(nearby.locations)
        : [],
    shoppingCenter:
      !supportedSelections || supportedSelections.includes('SELECTED_SHOPPING_CENTER')
        ? buildShoppingCenterResults(nearby.shoppingCenters)
        : [],
    brands:
      !supportedSelections || supportedSelections.includes('SELECTED_BRAND') ? buildBrandResults(nearby.brands) : [],
    addresses:
      !supportedSelections || supportedSelections.includes('SELECTED_ADDRESS')
        ? buildAddressResults(nearby.addresses)
        : [],
    parcels: !supportedSelections ? buildParcelResults(nearby.parcels) : [],
    owners: !supportedSelections ? buildEntityResults(nearby.entities) : [],
    other: !supportedSelections ? buildAddressResults(nearby.other) : [],
  }
  const byParcelsResults = {
    locations:
      !supportedSelections || supportedSelections.includes('SELECTED_LOCATION')
        ? buildLocationResults(byParcels.locations, { sort: true })
        : [],
    shoppingCenters:
      !supportedSelections || supportedSelections.includes('SELECTED_SHOPPING_CENTER')
        ? buildShoppingCenterResults(byParcels.shoppingCenters)
        : [],
    brands:
      !supportedSelections || supportedSelections.includes('SELECTED_BRAND') ? buildBrandResults(byParcels.brands) : [],
    parcels: !supportedSelections ? buildParcelResults(byParcels.parcels) : [],
    owners: !supportedSelections ? buildEntityResults(byParcels.entities) : [],
  }
  return {
    nearby: nearbyResults,
    byParcels: byParcelsResults,
  }
}
